import { useEffect, useRef, useState } from "react";
import type { SocialSignInInput } from "@atlas/shared";
import {
  APPLE_SERVICES_ID,
  GOOGLE_CLIENT_ID,
  SocialSignInCancelled,
  renderGoogleButton,
  signInWithApple,
} from "../lib/social-sign-in";

// Apple + Google rows for the login and signup pages and the account page
// "Link" actions (ATL-130). Renders nothing when neither provider is
// configured, so the pages fall back to email + Strava untouched. Apple's
// button follows the HIG white-on-dark variant (plain white, black mark,
// system font). Google's button is the official one rendered by GIS.

export const SOCIAL_SIGN_IN_AVAILABLE = !!APPLE_SERVICES_ID || !!GOOGLE_CLIENT_ID;

export function hasSocialProvider(provider: "apple" | "google"): boolean {
  return provider === "apple" ? !!APPLE_SERVICES_ID : !!GOOGLE_CLIENT_ID;
}

export function AppleMark({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 170 170" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.197-2.12-9.973-3.17-14.34-3.17-4.58 0-9.492 1.05-14.746 3.17-5.262 2.13-9.501 3.24-12.742 3.35-4.929.21-9.842-1.96-14.746-6.52-3.13-2.73-7.045-7.41-11.735-14.04-5.032-7.08-9.169-15.29-12.41-24.65-3.471-10.11-5.211-19.9-5.211-29.378 0-10.857 2.346-20.221 7.045-28.068 3.693-6.303 8.606-11.275 14.755-14.925s12.793-5.51 19.948-5.629c3.915 0 9.049 1.211 15.429 3.591 6.362 2.388 10.447 3.599 12.238 3.599 1.339 0 5.877-1.416 13.57-4.239 7.275-2.618 13.415-3.702 18.445-3.275 13.63 1.1 23.87 6.473 30.68 16.153-12.19 7.386-18.22 17.731-18.1 31.002.11 10.337 3.86 18.939 11.23 25.769 3.34 3.17 7.07 5.62 11.22 7.36-.9 2.61-1.85 5.11-2.86 7.51zM119.11 7.24c0 8.102-2.96 15.667-8.86 22.669-7.12 8.324-15.732 13.134-25.071 12.375a25.222 25.222 0 0 1-.188-3.07c0-7.778 3.386-16.102 9.399-22.908 3.002-3.446 6.82-6.311 11.45-8.597 4.62-2.252 8.99-3.497 13.1-3.71.12 1.083.17 2.166.17 3.24z"
      />
    </svg>
  );
}

interface Props {
  /** Called with a verified-by-provider credential ready to post to the API. */
  onCredential: (input: SocialSignInInput) => void;
  onError?: (message: string) => void;
  /** Disables the rows while a sign-in request is in flight. */
  pending?: boolean;
  /** Attribution carried on new accounts (ATL-134, spec 21). */
  inviteCode?: string;
  referralCode?: string;
  /** Button label verb; "continue" suits a shared login/signup row. */
  intent?: "signin" | "signup" | "continue";
  /** Which providers to offer (default both configured ones). */
  only?: Array<"apple" | "google">;
  /** Full-width rows by default; set for a narrower inline use. */
  width?: number;
}

export function SocialSignInButtons({
  onCredential,
  onError,
  pending = false,
  inviteCode,
  referralCode,
  intent = "continue",
  only,
  width,
}: Props) {
  const googleRef = useRef<HTMLDivElement>(null);
  const [appleBusy, setAppleBusy] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);
  const callbackRef = useRef(onCredential);
  callbackRef.current = onCredential;

  const showApple = !!APPLE_SERVICES_ID && (!only || only.includes("apple"));
  const showGoogle = !!GOOGLE_CLIENT_ID && (!only || only.includes("google"));

  const attribution = {
    ...(inviteCode ? { inviteCode } : {}),
    ...(referralCode ? { referralCode } : {}),
  };

  useEffect(() => {
    if (!showGoogle || !googleRef.current) return;
    const el = googleRef.current;
    const measured = width ?? el.getBoundingClientRect().width;
    let cancelled = false;
    renderGoogleButton(
      el,
      (idToken) => {
        if (cancelled) return;
        callbackRef.current({ provider: "google", idToken, ...attribution });
      },
      {
        width: measured > 0 ? measured : 320,
        text: intent === "signin" ? "signin_with" : intent === "signup" ? "signup_with" : "continue_with",
      },
    )
      .then(() => {
        if (!cancelled) setGoogleReady(true);
      })
      .catch(() => {
        if (!cancelled) onError?.("Google sign-in could not load. Try again later.");
      });
    return () => {
      cancelled = true;
    };
    // The GIS button only needs re-rendering when the provider, label or
    // attribution changes; onCredential is read through the ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showGoogle, intent, width, inviteCode, referralCode]);

  if (!showApple && !showGoogle) return null;

  const appleLabel =
    intent === "signin" ? "Sign in with Apple" : intent === "signup" ? "Sign up with Apple" : "Continue with Apple";

  async function handleApple() {
    if (appleBusy || pending) return;
    setAppleBusy(true);
    try {
      const cred = await signInWithApple();
      callbackRef.current({ provider: "apple", ...cred, ...attribution });
    } catch (err) {
      if (!(err instanceof SocialSignInCancelled)) {
        onError?.(err instanceof Error ? err.message : "Sign in with Apple did not complete.");
      }
    } finally {
      setAppleBusy(false);
    }
  }

  const disabled = pending || appleBusy;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
        width: width ?? "100%",
        opacity: pending ? 0.7 : 1,
        pointerEvents: pending ? "none" : "auto",
      }}
    >
      {showApple && (
        <button
          type="button"
          onClick={() => void handleApple()}
          disabled={disabled}
          aria-label={appleLabel}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            width: "100%",
            height: 44,
            background: "#ffffff",
            color: "#000000",
            border: "1px solid #ffffff",
            borderRadius: "var(--radius-md)",
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif',
            fontSize: 15,
            fontWeight: 600,
            letterSpacing: "-0.01em",
            cursor: disabled ? "not-allowed" : "pointer",
          }}
        >
          <AppleMark size={17} />
          {appleBusy ? "Waiting for Apple…" : appleLabel}
        </button>
      )}
      {showGoogle && (
        <div
          style={{
            position: "relative",
            width: "100%",
            minHeight: 44,
            display: "flex",
            justifyContent: "center",
          }}
        >
          {!googleReady && (
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                inset: 0,
                borderRadius: "var(--radius-md)",
                background: "var(--surface-sunken)",
                border: "1px solid var(--border-default)",
              }}
            />
          )}
          <div ref={googleRef} style={{ width: "100%", display: "flex", justifyContent: "center", position: "relative" }} />
        </div>
      )}
    </div>
  );
}
